/**
 * Right-side drawer for making a leave balance adjustment (Admin / HR Manager).
 * Reuses the existing ledger API (concurrency + idempotency + audit + notifications).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, AlertTriangle, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const REASON_OPTIONS = [
  { code: 'opening_balance_correction', label: 'Opening Balance Correction' },
  { code: 'carry_forward', label: 'Carry Forward' },
  { code: 'policy_adjustment', label: 'Policy Adjustment' },
  { code: 'manual_correction', label: 'Manual Correction' },
  { code: 'leave_cancellation_correction', label: 'Leave Cancellation Correction' },
  { code: 'approved_special_entitlement', label: 'Approved Special Entitlement' },
  { code: 'unpaid_leave_correction', label: 'Unpaid Leave Correction' },
  { code: 'data_migration_correction', label: 'Data Migration Correction' },
  { code: 'other', label: 'Other' },
];

const fmtDT = (d) => d ? formatDateTimeLocal(d) : '—';

const StatRow = ({ label, value, bold, valueCls = '' }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} text-slate-900 ${valueCls}`}>{value}</span>
  </div>
);

export const LeaveAdjustmentDrawer = ({ employee, leaveTypeId, leaveYear, caps, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [overrideChecked, setOverrideChecked] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState(null);
  const [recentLoading, setRecentLoading] = useState(true);
  const [showDiscard, setShowDiscard] = useState(false);

  const dirty = !!(amount || reasonCode || notes.trim());

  const requestClose = useCallback(() => {
    if (dirty) setShowDiscard(true);
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/leave-registry/adjustments`, {
          params: { user_id: employee.user_id, leave_type_id: leaveTypeId, year: leaveYear, page: 1, page_size: 3 }
        });
        setRecent(res.data);
      } catch (e) { setRecent({ adjustments: [], total_items: 0 }); }
      finally { setRecentLoading(false); }
    })();
  }, [employee.user_id, leaveTypeId, leaveYear]);

  const amt = parseFloat(amount);
  const amtValid = amount !== '' && !isNaN(amt) && amt !== 0;
  const reasonValid = reasonCode && (reasonCode !== 'other' || customReason.trim().length >= 5);
  const newBalance = amtValid ? +(employee.available_balance + amt).toFixed(2) : null;
  const needsOverride = newBalance !== null && newBalance < 0;
  const overrideValid = !needsOverride || (caps.can_negative_override && overrideChecked && overrideReason.trim().length >= 5);
  const formValid = amtValid && reasonValid && overrideValid;

  const finalReason = () => reasonCode === 'other'
    ? customReason.trim()
    : REASON_OPTIONS.find(r => r.code === reasonCode)?.label || reasonCode;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/leave-quota/adjust-balance`, {
        user_id: employee.user_id,
        leave_type_id: leaveTypeId,
        year: leaveYear,
        adjustment: amt,
        reason: finalReason(),
        reason_code: reasonCode,
        notes: notes.trim() || null,
        expected_current_balance: employee.available_balance,
        idempotency_key: `${employee.user_id}-${leaveTypeId}-${leaveYear}-${Date.now()}`,
        allow_negative_override: needsOverride && overrideChecked,
        override_reason: needsOverride && overrideChecked ? overrideReason.trim() : null,
      });
      const adj = res.data.adjustment;
      toast.success(`Adjusted ${employee.leave_type_name} for ${employee.employee_name}`, {
        description: `Previous balance: ${adj.previous_balance} → New balance: ${adj.new_balance}`,
      });
      onSaved(employee.user_id);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('Balance changed by another user', { description: e.response.data.detail });
        onSaved(null);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save adjustment');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="leave-adjustment-drawer" role="dialog" aria-modal="true" aria-label="Adjust leave balance">
      <div className="absolute inset-0 bg-black/30" onClick={requestClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 p-4 flex items-start justify-between shrink-0">
          <div>
            <h3 className="font-semibold text-slate-900">Adjust Leave Balance</h3>
            <p className="text-sm text-slate-700 mt-0.5">{employee.employee_name}</p>
            <p className="text-xs text-slate-400">{employee.email}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={requestClose} aria-label="Close drawer" data-testid="adjustment-drawer-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Context */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-xs" data-testid="drawer-emp-id">ID: {employee.employee_id || '—'}</Badge>
            <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">{employee.leave_type_name}</Badge>
            <Badge variant="outline" className="text-xs">{leaveYear}</Badge>
          </div>

          {/* Balance breakdown */}
          <div className="bg-slate-50 rounded-lg p-3 divide-y divide-slate-200" data-testid="drawer-balance-breakdown">
            <StatRow label="Entitlement" value={`${employee.entitlement} days`} />
            <StatRow label="Used" value={`${employee.used} days`} />
            <StatRow label="Pending approval" value={`${employee.pending} days`} />
            <StatRow label="Existing adjustments" value={`${employee.ledger_adjustments > 0 ? '+' : ''}${employee.ledger_adjustments} days`}
              valueCls={employee.ledger_adjustments > 0 ? 'text-emerald-600' : employee.ledger_adjustments < 0 ? 'text-rose-600' : ''} />
            <StatRow label="Available balance" value={`${employee.available_balance} days`} bold
              valueCls={employee.available_balance < 0 ? 'text-rose-600' : ''} />
          </div>

          {/* Recent adjustments */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Recent adjustments</p>
            {recentLoading ? (
              <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-blue-600" /></div>
            ) : recent?.adjustments?.length === 0 ? (
              <p className="text-xs text-slate-400">No adjustments recorded for this leave type and year.</p>
            ) : (
              <div className="space-y-1.5">
                {recent.adjustments.map(adj => (
                  <div key={adj.id} className="text-xs border border-slate-200 rounded-md px-2.5 py-1.5 flex items-center justify-between gap-2">
                    <span className={`font-semibold ${adj.adjustment >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {adj.adjustment >= 0 ? '+' : ''}{adj.adjustment}d
                    </span>
                    <span className="text-slate-500 truncate flex-1">{adj.reason}</span>
                    <span className="text-slate-400 whitespace-nowrap">{fmtDT(adj.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="space-y-3 border-t border-slate-200 pt-4">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="adj-drawer-amount">
                Adjustment amount (days) <span className="text-rose-500">*</span>
              </label>
              <Input id="adj-drawer-amount" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder={employee.allow_half_day ? 'e.g. +2 or -0.5' : 'e.g. +2 or -1'}
                className="h-9" data-testid="drawer-amount-input" />
              {amount !== '' && !amtValid && <p className="text-[11px] text-rose-600 mt-1">Enter a non-zero number (e.g. 2, -1, 0.5).</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Reason <span className="text-rose-500">*</span>
              </label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger className="h-9" data-testid="drawer-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                {/* z-[110]: drawer overlay is z-[100]; portal default z-50 rendered BEHIND it */}
                <SelectContent className="z-[110]">{REASON_OPTIONS.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              {reasonCode === 'other' && (
                <Input value={customReason} onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter reason (min 5 chars) *" className="h-9 mt-1.5 text-sm"
                  data-testid="drawer-custom-reason" />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="adj-drawer-notes">Notes (optional)</label>
              <Textarea id="adj-drawer-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional context for the audit trail..." rows={2} maxLength={500}
                className="text-sm resize-none" data-testid="drawer-notes-input" />
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3" data-testid="drawer-balance-preview">
            <StatRow label="Previous balance" value={`${employee.available_balance} days`} />
            <StatRow label="Proposed adjustment" value={amtValid ? `${amt > 0 ? '+' : ''}${amt} days` : '—'} />
            <StatRow label="New balance" bold
              value={newBalance !== null ? `${newBalance} days` : '—'}
              valueCls={newBalance !== null && newBalance < 0 ? 'text-rose-600' : 'text-emerald-700'} />
          </div>

          {/* Negative override */}
          {needsOverride && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2" data-testid="drawer-negative-warning">
              <p className="text-rose-700 text-sm font-medium flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> This creates a NEGATIVE balance
              </p>
              {caps.can_negative_override ? (
                <>
                  <label className="flex items-center gap-2 text-xs text-rose-700">
                    <Checkbox checked={overrideChecked} onCheckedChange={setOverrideChecked} data-testid="drawer-override-checkbox" />
                    I authorize this negative-balance override (Admin)
                  </label>
                  {overrideChecked && (
                    <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Override reason (mandatory, min 5 chars)" className="h-8 text-xs"
                      data-testid="drawer-override-reason" />
                  )}
                </>
              ) : (
                <p className="text-xs text-rose-600">Negative balances require an Admin override. This adjustment will be rejected.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex items-center justify-end gap-2 shrink-0 bg-white">
          <Button variant="outline" onClick={requestClose} data-testid="drawer-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!formValid || saving} className="gap-1.5" data-testid="drawer-confirm-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Confirm &amp; Save
          </Button>
        </div>
      </div>

      {/* Unsaved-changes confirmation — z-[110] to stack above the z-[100] drawer */}
      <Dialog open={showDiscard} onOpenChange={setShowDiscard}>
        <DialogContent className="max-w-sm z-[110]" data-testid="drawer-discard-dialog">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>You have an adjustment in progress. Closing the drawer will discard it.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscard(false)} data-testid="drawer-discard-cancel">Keep editing</Button>
            <Button variant="destructive" onClick={() => { setShowDiscard(false); onClose(); }} data-testid="drawer-discard-confirm">Discard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveAdjustmentDrawer;
